import React, { ReactNode, useEffect, useRef, useState } from 'react';
import { FormControl } from '@mui/material';
import logger from 'js-logger';
import { notifications } from '@shared/utils/notifications';
import DialogIconButton from './DialogIconButton';
import { tokens, useTranslation } from '../utils/i18n';
import NCALayer, { NcaLayerCancelledError, NcaLayerRequestError } from '../utils/helpers/ncalayer';

/** Подпись произвольных данных на уже установленном соединении с NCALayer. */
export type SignDataFn = (payload: string) => Promise<string>;

interface Props {
  data?: any;
  content: ReactNode;
  onPostSign?: (data: any) => Promise<void>;
  iin?: string;
  bin?: string;
}

/**
 * OSI-763: разбор ответа NCALayer на запрос sign.
 * Раньше отмена пользователем (status=true, но body.result отсутствует) молча игнорировалась —
 * кнопка оставалась «нажатой», пользователь не понимал, что произошло.
 */
const extractCms = (result: any): string => {
  if (!result?.status) {
    throw new NcaLayerRequestError(result?.message);
  }

  const raw = Array.isArray(result?.body?.result) ? result.body.result[0] : undefined;
  if (!raw) {
    throw new NcaLayerCancelledError();
  }

  return String(raw)
    .replace(/-----BEGIN CMS-----/, '')
    .replace(/-----END CMS-----/, '')
    .replace(/\r?\n/g, '')
    .trim();
};

const SignerBasic: React.FC<Props> = ({ data, content, onPostSign, iin, bin }) => {
  const [ncaAvailable, setNcaAvailable] = useState(false);
  // OSI-763: состояние загрузки, чтобы блокировать повторное нажатие и корректно
  // сниматься при отмене пользователем.
  const [isSigning, setIsSigning] = useState(false);
  const { translateToken: tt, getLocale } = useTranslation();

  // OSI-763: раньше это была обычная переменная в теле компонента — она пересоздавалась
  // на каждом рендере, и cleanup гасил флаг не того замыкания. Ref живёт весь монтаж.
  const closedRef = useRef(false);

  const [socket] = useState(new NCALayer(new WebSocket('wss://127.0.0.1:13579/')));

  const onConnect = () => {
    setNcaAvailable(true);
  };

  const onDisconnect = () => {
    setNcaAvailable(false);
    // OSI-763: текст уже переведён через tt(), поэтому toast, а не notifications.error
    // (тот прогоняет аргумент через i18next.t и на готовом тексте даёт missingKey).
    if (!closedRef.current) notifications.toast(tt(tokens.signer.notConnectToNCAlayer), 'error');
  };

  const onMessage = () => {};

  useEffect(() => {
    socket.on('connect', onConnect);
    socket.on('disconnect', onDisconnect);
    socket.on('message', onMessage);

    // Закрываем гонку: WebSocket к localhost открывается почти мгновенно и событие 'connect'
    // (emit при onopen) может произойти ДО подписки выше. EventEmitter не буферизует прошлые
    // события, поэтому проверяем актуальное состояние сокета и активируем кнопку, если уже открыт.
    if (socket.isOpen) {
      onConnect();
    }

    return function cleanup() {
      closedRef.current = true;
      socket.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket]);

  const buildSignRequest = (payload: string) => ({
    module: 'kz.gov.pki.knca.basics',
    method: 'sign',
    args: {
      // allowedStorages: ['PKCS12'],
      format: 'cms',
      data: payload,
      signingParams: { decode: 'true', digested: 'false', encapsulate: 'true', tsaProfile: null },
      signerParams: {
        extKeyUsageOids: [],
        iin,
        bin,
        serialNumber: '',
        chain: null
      },
      locale: getLocale() === 'kz' ? 'kk' : getLocale()
    }
  });

  // OSI-763: подпись произвольных данных. Ответ соотносится именно с этим запросом
  // (промис-обёртка транспорта), поэтому параллельные подписи не перепутаются.
  const signData: SignDataFn = async (payload: string) => {
    const result = await socket.request(buildSignRequest(payload));
    return extractCms(result);
  };

  const handleAgree = () => {
    if (isSigning) return;

    setIsSigning(true);

    void (async () => {
      try {
        // OSI-763: подписание однофазное — подписываем data и отдаём CMS наружу.
        const cms = await signData(data);
        await onPostSign?.(cms);
      } catch (e) {
        // OSI-763: отмена и ошибки NCALayer теперь всегда доходят до пользователя.
        if (e instanceof NcaLayerCancelledError) {
          notifications.toast(tt(tokens.signer.signCancelled), 'warning');
        } else if (e instanceof NcaLayerRequestError) {
          notifications.toast(`${tt(tokens.signer.ncaLayerError)}: ${e.message}`, 'error');
        } else {
          logger.error(e);
          notifications.toast(tt(tokens.signer.signError), 'error');
        }
      } finally {
        // OSI-763: снимаем состояние загрузки в любом исходе, включая отмену.
        setIsSigning(false);
      }
    })();
  };

  return (
    <FormControl>
      <DialogIconButton
        label={tt(tokens.signer.sign)}
        title={tt(tokens.signer.dialogTite)}
        content={content}
        onAgree={handleAgree}
        disabled={!ncaAvailable || isSigning || !data}
      />
    </FormControl>
  );
};

export default SignerBasic;
